import { Divider } from '@sunwebgroup/uds-ui/divider'
import { TextField } from '@sunwebgroup/uds-ui/fields/text-field'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import * as React from 'react'
import { useIntl } from '@/intl'
import {
  FullPaymentReminderRow,
  RemainingDepositRow,
} from '@/scopes/payment/components/PaymentReminders/PaymentReminders'
import styles from './DepositDetails.module.css'

interface DepositDetailsProps {
  minimumDeposit: number
  totalPrice: number
  value: number | null
  onChange: (amount: number) => void
  dueDate?: string
  fullPaymentDueDate?: string
}

export function DepositDetails({
  minimumDeposit,
  totalPrice,
  value,
  onChange,
  dueDate,
  fullPaymentDueDate,
}: DepositDetailsProps) {
  const intl = useIntl()
  const dueAmount = Math.min(totalPrice, Math.max(minimumDeposit, value ?? minimumDeposit))
  const remainingTotalAmount = totalPrice - dueAmount
  const remainingDueAmount = dueAmount - (value ?? minimumDeposit)

  const [isFocused, setIsFocused] = React.useState(false)
  const [rawInput, setRawInput] = React.useState(String(dueAmount))

  React.useEffect(() => {
    if (!isFocused) {
      setRawInput(String(value ?? minimumDeposit))
    }
  }, [value, minimumDeposit, isFocused])

  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    setIsFocused(true)
    setRawInput(String(dueAmount))
    e.target.select()
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setRawInput(e.target.value)
  }

  function handleBlur() {
    setIsFocused(false)
    const normalised = rawInput.replace(',', '.')
    const parsed = Number.parseFloat(normalised)
    if (!Number.isNaN(parsed)) {
      const clamped = Math.min(totalPrice, Math.max(minimumDeposit, parsed))
      onChange(clamped)
    }
    else {
      onChange(minimumDeposit)
    }
  }

  const inputDisplayValue = isFocused ? rawInput : intl.formatCurrency(dueAmount)

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <Typography variant="body-m-regular-snug">
          {intl.formatMessage({ id: 'payment_scope_deposit_minimum_label', defaultMessage: 'Minimum deposit' })}
        </Typography>
        <Typography variant="body-m-regular-snug">{intl.formatCurrency(minimumDeposit)}</Typography>
      </div>

      <div className={styles.fieldWrapper}>
        <TextField
          name="deposit-custom-amount"
          label={intl.formatMessage({
            id: 'payment_scope_deposit_pay_more_label',
            defaultMessage: 'Want to pay more today?',
          })}
          value={inputDisplayValue}
          onFocus={handleFocus}
          onChange={handleChange}
          onBlur={handleBlur}
          inputProps={{ inputMode: 'decimal' }}
          formFieldClass={cn(styles.customAmountFieldWrapper, styles.formFieldXXS)}
        />
      </div>

      <Divider className={styles.separator} />

      <div aria-live="polite" aria-atomic="true" className={styles.reminderRows}>
        <div className={styles.summaryRow}>
          <Typography variant="body-l-bold-snug">
            {intl.formatMessage({ id: 'payment_scope_deposit_due_today_label', defaultMessage: 'Due today' })}
          </Typography>
          <Typography variant="body-m-bold-snug">{intl.formatCurrency(dueAmount)}</Typography>
        </div>
        <RemainingDepositRow remainingDeposit={remainingDueAmount} dueDate={dueDate} />
        <FullPaymentReminderRow remainingTotalAmount={remainingTotalAmount} fullPaymentDueDate={fullPaymentDueDate} />
      </div>
    </div>
  )
}
